import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ThemeToggle } from '../components/ThemeToggle';
import LanguageSelector from '../components/LanguageSelector';
import { Eye, EyeOff, ArrowRight, AlertCircle, ArrowLeft, Briefcase, Heart, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_agency-network-hub/artifacts/d66f2cg2_IMG_0019.png";

const CARE_IMAGES = [
  { url: "https://images.pexels.com/photos/18459702/pexels-photo-18459702.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=800", caption: "Compassionate elderly care", label: "Elderly Care" },
  { url: "https://images.unsplash.com/photo-1765896387398-1e1ae8d2eb85?fit=crop&crop=faces&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=900&h=1400", caption: "Building meaningful connections", label: "Senior Support" },
  { url: "https://images.pexels.com/photos/6284845/pexels-photo-6284845.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=800", caption: "Supporting independence every day", label: "Disability Care" },
  { url: "https://images.pexels.com/photos/8415495/pexels-photo-8415495.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=800", caption: "Empowering mobility and freedom", label: "Accessibility Support" },
  { url: "https://images.pexels.com/photos/27177471/pexels-photo-27177471.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=800", caption: "Nurturing the next generation", label: "Childcare" },
  { url: "https://images.pexels.com/photos/4017421/pexels-photo-4017421.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=800", caption: "Joyful moments in early learning", label: "Early Years" },
];

const ROLES = [
  { value: 'job_seeker', label: 'Job Seeker', description: 'Looking for caregiver positions', icon: Briefcase, color: '#00A6A0' },
  { value: 'potential_client', label: 'Care Seeker', description: 'Looking for care services', icon: Heart, color: '#FF6F61' },
  { value: 'family', label: 'Family Member', description: 'Monitor care for a loved one', icon: Users, color: '#4A90E2' },
];

export function SignUp() {
  const [step, setStep] = useState(1);
  const [selectedRole, setSelectedRole] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [currentImageIdx, setCurrentImageIdx] = useState(() => Math.floor(Math.random() * CARE_IMAGES.length));

  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIdx(prev => (prev + 1) % CARE_IMAGES.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setError('');
    setIsLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name, email, phone, password, role: selectedRole }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Registration failed');

      // Auto-login after registration
      const loginResult = await login(email, password);
      if (loginResult.success) {
        if (selectedRole === 'job_seeker') {
          navigate('/jobs');
        } else {
          navigate('/dashboard');
        }
      } else {
        navigate('/login');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between gap-2 mb-8 flex-wrap">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="FendanaCura" className="h-8 sm:h-10 w-auto" />
              <span className="text-lg sm:text-xl font-heading font-bold text-foreground">FendanaCura</span>
            </Link>
            <div className="flex items-center gap-2">
              <LanguageSelector />
              <ThemeToggle />
            </div>
          </div>

          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="back-to-home-link">
            <ArrowLeft className="w-4 h-4" />
            Back to homepage
          </Link>

          {step === 1 ? (
            <>
              <div className="mb-8">
                <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground mb-2">
                  Create your account
                </h1>
                <p className="text-muted-foreground">
                  Choose how you'd like to use FendanaCura
                </p>
              </div>

              <div className="space-y-3 mb-8">
                {ROLES.map((role) => {
                  const Icon = role.icon;
                  const isSelected = selectedRole === role.value;
                  return (
                    <button
                      key={role.value}
                      onClick={() => setSelectedRole(role.value)}
                      className={`w-full p-4 rounded-xl text-left transition-all flex items-center gap-4 border ${
                        isSelected
                          ? 'border-[#00A6A0] bg-[#00A6A0]/5'
                          : 'border-border bg-card hover:border-muted-foreground/30'
                      }`}
                      data-testid={`role-${role.value}`}
                    >
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${role.color}15` }}>
                        <Icon className="w-5 h-5" style={{ color: role.color }} />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{role.label}</p>
                        <p className="text-sm text-muted-foreground">{role.description}</p>
                      </div>
                      <div className={`ml-auto w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        isSelected ? 'border-[#00A6A0]' : 'border-border'
                      }`}>
                        {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-[#00A6A0]" />}
                      </div>
                    </button>
                  );
                })}
              </div>

              <Button
                onClick={() => { if (selectedRole) setStep(2); }}
                disabled={!selectedRole}
                className="w-full btn-primary py-6"
                data-testid="continue-btn"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>

              <div className="mt-6 text-center space-y-2">
                <p className="text-sm text-muted-foreground">
                  Want to register an agency?{' '}
                  <Link to="/register-company" className="text-[#00A6A0] hover:underline font-medium" data-testid="register-agency-link">
                    Register your agency
                  </Link>
                </p>
                <p className="text-sm text-muted-foreground">
                  Already have an account?{' '}
                  <Link to="/login" className="text-[#4A90E2] hover:underline font-medium" data-testid="login-link">
                    Sign in
                  </Link>
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="mb-8">
                <button onClick={() => setStep(1)} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
                  <ArrowLeft className="w-3.5 h-3.5" /> Change role
                </button>
                <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground mb-2">
                  Your details
                </h1>
                <p className="text-muted-foreground">
                  Signing up as <span className="text-[#00A6A0] font-medium">{ROLES.find(r => r.value === selectedRole)?.label}</span>
                </p>
              </div>

              {error && (
                <div className="mb-6 p-4 rounded-lg bg-destructive/10 border border-destructive/20 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                  <p className="text-destructive text-sm">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-foreground">Full Name</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" required className="bg-card border-border" data-testid="signup-name-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground">Email</Label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required className="bg-card border-border" data-testid="signup-email-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground">Phone</Label>
                  <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+44 7xxx xxx xxx" className="bg-card border-border" data-testid="signup-phone-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground">Password</Label>
                  <div className="relative">
                    <Input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters" required className="bg-card border-border pr-10" data-testid="signup-password-input" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground">Confirm Password</Label>
                  <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Re-enter password" required className="bg-card border-border" data-testid="signup-confirm-input" />
                </div>
                <Button type="submit" disabled={isLoading} className="w-full btn-primary py-6" data-testid="signup-submit-btn">
                  {isLoading ? <span className="flex items-center gap-2"><div className="spinner w-4 h-4" />Creating account...</span> :
                  <span className="flex items-center gap-2">Create Account<ArrowRight className="w-4 h-4" /></span>}
                </Button>
              </form>

              <p className="mt-6 text-center text-sm text-muted-foreground">
                Already have an account?{' '}
                <Link to="/login" className="text-[#4A90E2] hover:underline font-medium">Sign in</Link>
              </p>
            </>
          )}
        </div>
      </div>

      {/* Right Panel - Rotating Care Images */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden" data-testid="auth-image-panel">
        {CARE_IMAGES.map((img, idx) => (
          <div
            key={idx}
            className="absolute inset-0 transition-opacity duration-1000"
            style={{ opacity: idx === currentImageIdx ? 1 : 0 }}
          >
            <img
              src={img.url}
              alt={img.caption}
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent via-background/30 to-background" />
          </div>
        ))}
        <div className="absolute bottom-12 left-12 right-12 p-8 rounded-2xl glass-effect border border-border z-20">
          <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-[#00A6A0]/20 text-[#00A6A0] mb-3">
            {CARE_IMAGES[currentImageIdx].label}
          </span>
          <p className="text-lg text-foreground font-medium mb-2">
            {CARE_IMAGES[currentImageIdx].caption}
          </p>
          <p className="text-muted-foreground text-sm">
            "Joining FendanaCura connected me with the right agencies instantly."
          </p>
        </div>
        {/* Image dots */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
          {CARE_IMAGES.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentImageIdx(idx)}
              className={`w-2 h-2 rounded-full transition-all ${idx === currentImageIdx ? 'bg-[#00A6A0] w-6' : 'bg-white/40'}`}
              aria-label={`Show image ${idx + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default SignUp;
